/**
 * Page Chauffage ECS - HAL 3000
 * Version complète avec tous les appareils Saint-Georges
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import Layout from '../components/Layout';
import { api } from '@/App';
import { 
  Flame, Thermometer, Droplets, Power, Leaf, Plus, Minus,
  RefreshCw, AlertTriangle, Settings
} from 'lucide-react';

const SAINT_GEORGES_OWNER_ID = 26773026;

// Logos en base64 ou URLs
const LOGOS = {
  viessmann: 'https://upload.wikimedia.org/wikipedia/commons/8/80/Viessmann_logo.svg',
  totalEnergies: 'https://upload.wikimedia.org/wikipedia/commons/8/89/TotalEnergies_logo.svg',
  italia: '🇮🇹',
  tuya: 'https://images.tuyacn.com/content-platform/hestia/16423082640c12c86df50.png',
};

/**
 * Vanne Ronde Style Nest - INTERACTIVE
 */
const NestDial = ({ 
  name, 
  temperature, 
  targetTemp,
  isOn, 
  online = true,
  isPlaceholder = false,
  size = 'medium',
  subtitle,
  onToggle,
  onSetTemp,
  onModeChange,
  mode,
  minTemp = 5,
  maxTemp = 30,
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [localTemp, setLocalTemp] = useState(targetTemp);
  const dialRef = useRef(null);

  const sizes = {
    large: { dial: 140, stroke: 8, fontSize: 34, labelSize: 11 },
    medium: { dial: 95, stroke: 6, fontSize: 24, labelSize: 9 },
    small: { dial: 75, stroke: 5, fontSize: 20, labelSize: 8 },
  };
  const s = sizes[size] || sizes.medium;
  const cx = s.dial / 2;
  const cy = s.dial / 2;
  const r = (s.dial - s.stroke * 2) / 2;

  const tempToAngle = (temp) => {
    if (temp === null || temp === undefined) return -135;
    const normalized = Math.max(0, Math.min(1, (temp - minTemp) / (maxTemp - minTemp)));
    return -135 + (normalized * 270);
  };
  
  const angleToTemp = (angle) => {
    const normalized = (angle + 135) / 270;
    return Math.round(minTemp + normalized * (maxTemp - minTemp));
  };

  const describeArc = (startAngle, endAngle) => {
    const start = {
      x: cx + r * Math.cos((startAngle - 90) * Math.PI / 180),
      y: cy + r * Math.sin((startAngle - 90) * Math.PI / 180)
    };
    const end = {
      x: cx + r * Math.cos((endAngle - 90) * Math.PI / 180),
      y: cy + r * Math.sin((endAngle - 90) * Math.PI / 180)
    };
    const largeArc = endAngle - startAngle > 180 ? 1 : 0;
    return `M ${start.x} ${start.y} A ${r} ${r} 0 ${largeArc} 1 ${end.x} ${end.y}`;
  };

  const handleInteraction = useCallback((clientX, clientY) => {
    if (!dialRef.current || !onSetTemp || isPlaceholder || !online) return;
    
    const rect = dialRef.current.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    
    const dx = clientX - centerX;
    const dy = clientY - centerY;
    let angle = Math.atan2(dy, dx) * (180 / Math.PI) + 90;
    
    if (angle < -135) angle = -135;
    if (angle > 135) angle = 135;
    
    const newTemp = angleToTemp(angle);
    if (newTemp >= minTemp && newTemp <= maxTemp) {
      setLocalTemp(newTemp);
    }
  }, [onSetTemp, isPlaceholder, online, minTemp, maxTemp]);

  const handleStart = useCallback((e) => {
    if (!onSetTemp || isPlaceholder || !online) return;
    e.preventDefault();
    setIsDragging(true);
    const touch = e.touches ? e.touches[0] : e;
    handleInteraction(touch.clientX, touch.clientY);
  }, [onSetTemp, isPlaceholder, online, handleInteraction]);

  const handleMove = useCallback((e) => {
    if (!isDragging) return;
    e.preventDefault();
    const touch = e.touches ? e.touches[0] : e;
    handleInteraction(touch.clientX, touch.clientY);
  }, [isDragging, handleInteraction]);

  const handleEnd = useCallback(async () => {
    if (isDragging && localTemp !== null && onSetTemp && localTemp !== targetTemp) {
      await onSetTemp(localTemp);
    }
    setIsDragging(false);
  }, [isDragging, localTemp, onSetTemp, targetTemp]);

  useEffect(() => {
    if (isDragging) {
      const opts = { passive: false };
      window.addEventListener('mousemove', handleMove, opts);
      window.addEventListener('mouseup', handleEnd);
      window.addEventListener('touchmove', handleMove, opts);
      window.addEventListener('touchend', handleEnd);
      return () => {
        window.removeEventListener('mousemove', handleMove);
        window.removeEventListener('mouseup', handleEnd);
        window.removeEventListener('touchmove', handleMove);
        window.removeEventListener('touchend', handleEnd);
      };
    }
  }, [isDragging, handleMove, handleEnd]);

  useEffect(() => {
    if (!isDragging) setLocalTemp(targetTemp);
  }, [targetTemp, isDragging]);

  const getColor = () => {
    if (!online && !isPlaceholder) return '#666';
    if (isOn) return '#f97316';
    const t = temperature || localTemp || 15;
    if (t < 15) return '#3b82f6';
    if (t < 19) return '#22c55e';
    return '#f97316';
  };
  
  const color = getColor();
  const displayTemp = isDragging ? localTemp : (targetTemp ?? temperature);
  const displayAngle = tempToAngle(displayTemp);

  return (
    <div className="flex flex-col items-center">
      <div 
        ref={dialRef}
        className={`relative rounded-full ${isPlaceholder ? 'opacity-40' : ''} ${onSetTemp && online ? 'cursor-grab active:cursor-grabbing' : ''}`}
        style={{ 
          width: s.dial, 
          height: s.dial,
          background: isOn 
            ? `radial-gradient(circle at 30% 30%, ${color}50, ${color}20, #0a0a0a)`
            : `radial-gradient(circle at 30% 30%, ${color}25, #0a0a0a)`,
          boxShadow: isDragging 
            ? `0 0 50px ${color}80, inset 0 0 30px ${color}30` 
            : (isOn ? `0 0 30px ${color}50` : 'inset 0 2px 10px rgba(0,0,0,0.6)'),
          border: `3px solid ${isDragging ? '#fff' : color}`,
          touchAction: 'none',
          transition: isDragging ? 'none' : 'all 0.3s',
        }}
        onMouseDown={handleStart}
        onTouchStart={handleStart}
      >
        <svg width={s.dial} height={s.dial} className="absolute inset-0">
          <path d={describeArc(-135, 135)} fill="none" stroke="#333" strokeWidth={s.stroke} strokeLinecap="round" />
          <path 
            d={describeArc(-135, displayAngle)} 
            fill="none" 
            stroke={color} 
            strokeWidth={s.stroke} 
            strokeLinecap="round"
            style={{ filter: `drop-shadow(0 0 5px ${color})` }}
          />
        </svg>

        <div className="absolute inset-0 flex flex-col items-center justify-center">
          {subtitle && (
            <div className="text-[9px] font-mono text-gray-400 uppercase tracking-wide">{subtitle}</div>
          )}
          <div 
            className="font-bold font-mono leading-none"
            style={{ 
              fontSize: s.fontSize, 
              color: '#fff', 
              textShadow: isDragging ? `0 0 20px ${color}` : `0 0 10px ${color}80` 
            }}
          >
            {displayTemp !== null && displayTemp !== undefined ? Math.round(displayTemp) : '--'}
            <span style={{ fontSize: s.fontSize * 0.4 }}>°</span>
          </div>
          {temperature !== null && targetTemp !== null && Math.abs(temperature - targetTemp) > 1 && !isDragging && (
            <div className="text-[10px] font-mono text-gray-500 mt-0.5">
              {Math.round(temperature)}° actuel
            </div>
          )}
          {isOn && !isDragging && <Leaf className="w-3 h-3 text-green-400 mt-1" />}
        </div>
      </div>

      <div className="mt-1.5 text-center">
        <div 
          className="font-mono font-bold uppercase text-white tracking-wide"
          style={{ fontSize: s.labelSize, maxWidth: s.dial + 20 }}
        >
          {name}
        </div>
        {!online && !isPlaceholder && (
          <div className="text-red-400 font-mono text-[9px] animate-pulse">HORS LIGNE</div>
        )}
      </div>
      
      {onSetTemp && online && !isPlaceholder && (
        <div className="flex items-center gap-3 mt-1.5">
          <button 
            onClick={() => onSetTemp(Math.max(minTemp, (localTemp ?? targetTemp ?? 15) - 1))}
            className="w-7 h-7 rounded-full bg-gray-800 hover:bg-gray-700 border border-gray-600 flex items-center justify-center active:scale-90"
          >
            <Minus className="w-3 h-3 text-white" />
          </button>
          <button 
            onClick={() => onSetTemp(Math.min(maxTemp, (localTemp ?? targetTemp ?? 15) + 1))}
            className="w-7 h-7 rounded-full bg-gray-800 hover:bg-gray-700 border border-gray-600 flex items-center justify-center active:scale-90"
          >
            <Plus className="w-3 h-3 text-white" />
          </button>
        </div>
      )}
      
      {/* LOW/HIGH Mode Buttons */}
      {onModeChange && online && !isPlaceholder && (
        <div className="flex items-center gap-1 mt-1.5">
          <button
            onClick={() => onModeChange('low')}
            className={`px-2 py-0.5 rounded text-[9px] font-mono font-bold transition-all ${
              mode === 'low' ? 'bg-blue-500 text-white' : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
            }`}
          >
            LOW
          </button>
          <button
            onClick={() => onModeChange('high')}
            className={`px-2 py-0.5 rounded text-[9px] font-mono font-bold transition-all ${
              mode === 'high' ? 'bg-orange-500 text-white' : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
            }`}
          >
            HIGH
          </button>
        </div>
      )}
      
      {onToggle && online && (
        <button
          onClick={onToggle}
          className={`mt-1.5 px-3 py-1 rounded-full text-[10px] font-mono font-bold transition-all ${
            isOn ? 'bg-orange-600 text-white shadow-lg shadow-orange-500/30' : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
          }`}
        >
          {isOn ? 'ON' : 'OFF'}
        </button>
      )}
    </div>
  );
};

/**
 * Switch Industriel
 */
const IndustrialSwitch = ({ isOn, onChange, disabled, label, sublabel }) => (
  <div className="flex items-center gap-3">
    <button
      onClick={onChange}
      disabled={disabled}
      className={`relative w-14 h-7 rounded transition-all ${disabled ? 'opacity-50' : ''}`}
      style={{
        background: isOn ? '#1c1c1c' : '#0c0c0c',
        border: `2px solid ${isOn ? '#f97316' : '#444'}`,
        boxShadow: isOn ? '0 0 15px rgba(249, 115, 22, 0.4)' : 'inset 0 2px 4px rgba(0,0,0,0.5)',
      }}
    >
      <div 
        className={`absolute top-0.5 w-5 h-5 rounded transition-all ${isOn ? 'right-0.5 bg-orange-500' : 'left-0.5 bg-gray-600'}`}
        style={{ boxShadow: '0 2px 4px rgba(0,0,0,0.5)' }}
      />
    </button>
    <div className="font-mono">
      <div className={`text-base font-bold ${isOn ? 'text-orange-400' : 'text-gray-500'}`}>{isOn ? 'ON' : 'OFF'}</div>
      {label && <div className="text-[9px] text-gray-500">{label}</div>}
    </div>
  </div>
);

/**
 * Composant Consommation Gaz/Électricité avec défilement jour/semaine/mois/année
 */
const GasConsumptionCard = ({ title, data, pricePerKwh = 0.16, color = 'orange' }) => {
  const [periodIndex, setPeriodIndex] = useState(0);
  const periods = ['today', 'week', 'month', 'year'];
  const periodLabels = ['JOUR', 'SEMAINE', 'MOIS', 'ANNÉE'];
  
  useEffect(() => {
    const interval = setInterval(() => {
      setPeriodIndex(prev => (prev + 1) % 4);
    }, 3000);
    return () => clearInterval(interval);
  }, []);
  
  const currentPeriod = periods[periodIndex];
  const value = data?.[currentPeriod] ?? null;
  
  const colorClass = color === 'orange' ? 'text-orange-400' : color === 'blue' ? 'text-blue-400' : 'text-cyan-400';
  
  return (
    <div className="p-2 rounded bg-black/40 border border-gray-800">
      <div className="flex items-center justify-between mb-1">
        <div className="text-[8px] text-gray-500 font-mono">{title}</div>
        <div className="flex gap-0.5">
          {[0, 1, 2, 3].map(i => (
            <div 
              key={i} 
              className={`w-1 h-1 rounded-full ${i === periodIndex ? 'bg-cyan-400' : 'bg-gray-700'}`}
            />
          ))}
        </div>
      </div>
      <div className="text-[9px] text-gray-500 font-mono mb-1">{periodLabels[periodIndex]}</div>
      <div className="flex items-baseline gap-2">
        <span className={`text-base font-mono font-bold ${colorClass}`}>
          {value !== null ? value : '--'} <span className="text-[10px]">kWh</span>
        </span>
        <span className="text-sm font-mono text-green-400">
          {value !== null ? (value * pricePerKwh).toFixed(2) : '--'}€
        </span>
      </div>
    </div>
  );
};

/**
 * Composant Radiateur/Vanne unifié - Cercle plein avec design IMPACT
 * Couleur: ROUGE si actif/allumé, sinon couleur basée sur la température
 * 
 * IMPORTANT: Pour les radiateurs avec thermostat:
 * - temperature = température ACTUELLE de la pièce
 * - targetTemp = température CIBLE (consigne)
 * - Les boutons +/- modifient la CIBLE (targetTemp)
 */
const HeaterDial = ({ 
  name, 
  temperature,  // Température ACTUELLE
  targetTemp,   // Température CIBLE (consigne)
  isOn = true,
  online = true,
  onSetTemp,
  onToggle,
  minTemp = 5,
  maxTemp = 25,
  showTargetAsActual = false, // Pour Viessmann: afficher target au centre comme "cible"
  subtitle,
}) => {
  const size = 70;
  const cx = size / 2;
  const cy = size / 2;
  const r = 30;
  
  // Couleur: ROUGE si actif/allumé, sinon basée sur la température actuelle
  const getColor = () => {
    if (!online) return '#666';
    
    // Si allumé/actif → ROUGE
    if (isOn) return '#dc2626'; // Rouge vif
    
    // Si éteint → couleur basée sur la température actuelle
    const t = temperature ?? 15;
    if (t < 12) return '#3b82f6'; // Bleu froid
    if (t < 17) return '#22c55e'; // Vert moyen
    return '#f97316'; // Orange chaud
  };
  
  const color = getColor();

  return (
    <div className="flex flex-col items-center">
      <div 
        className="relative"
        style={{ width: size, height: size }}
      >
        <svg width={size} height={size}>
          {/* Cercle plein - PAS de curseur ni de repères */}
          <circle 
            cx={cx} 
            cy={cy} 
            r={r} 
            fill={color}
            style={{ 
              filter: online ? `drop-shadow(0 0 12px ${color}80)` : 'none',
              transition: 'fill 0.3s'
            }}
          />
        </svg>
        
        {/* Texte au centre - IMPACT */}
        <div 
          className="absolute inset-0 flex flex-col items-center justify-center text-white"
          style={{ fontFamily: 'Impact, sans-serif' }}
        >
          {/* Température principale en gros = RÉELLE */}
          <div style={{ fontSize: '22px', lineHeight: 1, letterSpacing: '1px' }}>
            {temperature !== null && temperature !== undefined 
              ? Math.round(Math.min(temperature, 99)) 
              : '--'}
          </div>
          {/* Température secondaire en petit = CIBLE si disponible */}
          {targetTemp !== null && targetTemp !== undefined && (
            <div style={{ fontSize: '9px', opacity: 0.7, marginTop: '1px' }}>
              cible {Math.round(Math.min(targetTemp, 21))}
            </div>
          )}
        </div>
      </div>
      
      {/* Nom - supporte les sauts de ligne avec \n */}
      <div 
        className="mt-0.5 text-center text-white uppercase tracking-wide whitespace-pre-line"
        style={{ fontSize: '9px', fontFamily: 'Impact, sans-serif', maxWidth: size + 20, lineHeight: 1.1 }}
      >
        {name}
        {subtitle && <div className="text-[7px] text-gray-400 normal-case">{subtitle}</div>}
      </div>
      
      {/* Boutons +/- pour température CIBLE */}
      {onSetTemp && online && (
        <div className="flex items-center gap-1 mt-0.5">
          <button 
            onClick={() => onSetTemp(Math.max(minTemp, (targetTemp ?? temperature ?? 15) - 1))}
            className="w-5 h-5 rounded-full bg-gray-800 hover:bg-gray-700 border border-gray-600 flex items-center justify-center active:scale-90"
          >
            <Minus className="w-2.5 h-2.5 text-white" />
          </button>
          <button 
            onClick={() => onSetTemp(Math.min(maxTemp, (targetTemp ?? temperature ?? 15) + 1))}
            className="w-5 h-5 rounded-full bg-gray-800 hover:bg-gray-700 border border-gray-600 flex items-center justify-center active:scale-90"
          >
            <Plus className="w-2.5 h-2.5 text-white" />
          </button>
        </div>
      )}
      
      {/* Bouton ON/OFF */}
      {onToggle && online && (
        <button
          onClick={onToggle}
          className={`mt-0.5 px-2 py-0.5 rounded text-[8px] font-bold transition-all ${
            isOn ? 'bg-orange-600 text-white shadow-lg shadow-orange-500/30' : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
          }`}
          style={{ fontFamily: 'Impact, sans-serif' }}
        >
          {isOn ? 'ON' : 'OFF'}
        </button>
      )}
      
      {!online && (
        <div className="text-red-400 font-mono text-[8px] animate-pulse mt-0.5">HORS LIGNE</div>
      )}
    </div>
  );
};

/**
 * Page Principale
 */
const HeatingPage = () => {
  const [loading, setLoading] = useState(true);
  const [vicare, setVicare] = useState(null);
  const [heatingDevices, setHeatingDevices] = useState([]);
  const [waterHeaters, setWaterHeaters] = useState([]);
  const [valves, setValves] = useState([]);
  const [refreshing, setRefreshing] = useState(false);
  const [globalActionLoading, setGlobalActionLoading] = useState(false);
  const [fortLamy, setFortLamy] = useState(null);
  const [pelletStove, setPelletStove] = useState(null);

  const loadData = useCallback(async () => {
    try {
      const [vicareRes, tuyaRes, fortLamyRes, pelletRes] = await Promise.all([
        api.get('/integrations/vicare/status').catch(() => ({ data: null })),
        api.get('/integrations/tuya/devices').catch(() => ({ data: null })),
        api.get('/integrations/tuya/fort-lamy/status').catch(() => ({ data: null })),
        api.get('/integrations/pellet-stove/status').catch(() => ({ data: null })),
      ]);

      if (vicareRes.data) setVicare(vicareRes.data);
      if (fortLamyRes.data?.success) setFortLamy(fortLamyRes.data);
      if (pelletRes.data) setPelletStove(pelletRes.data);

      if (tuyaRes.data?.success) {
        // Accepter tous les appareils (owner_id peut être absent ou 26773026)
        // Le filtrage Versailles se fait par device_id dans le backend
        const devices = tuyaRes.data.devices;

        // === Créer une map des capteurs de température par nom de pièce ===
        const tempSensors = {};
        devices.filter(d => d.category === 'wsdcg').forEach(d => {
          const status = {};
          (d.status || []).forEach(s => { status[s.code] = s.value; });
          let temp = status.va_temperature;
          if (temp !== undefined) {
            temp = temp / 10; // Les capteurs utilisent des dixièmes
          }
          const sensorName = d.name?.toLowerCase() || '';
          tempSensors[sensorName] = {
            temperature: temp ?? null,
            humidity: status.va_humidity ?? null,
            online: d.online,
          };
        });

        // === RADIATEURS & CHAUFFAGES ===
        // qn = thermostats, wk = vannes thermostatiques, cz/tdq = prises connectées pour chauffage
        const heaters = [];
        
        // Thermostats (qn) - température réglable avec LOW/HIGH
        devices.filter(d => d.category === 'qn').forEach(d => {
          const status = {};
          (d.status || []).forEach(s => { status[s.code] = s.value; });
          
          let temp = status.temp_current;
          let target = status.temp_set;
          // Les thermostats qn utilisent des valeurs directes (pas dixièmes)
          if (temp !== undefined && temp > 100) temp = temp / 10;
          if (target !== undefined && target > 100) target = target / 10;
          
          // Déterminer le mode LOW/HIGH basé sur la température cible
          let mode = 'manual';
          if (target !== undefined) {
            if (target <= 10) mode = 'low';
            else if (target >= 18) mode = 'high';
          }
          
          heaters.push({
            id: d.id,
            name: d.name,
            category: 'qn',
            online: d.online,
            temperature: temp ?? null,
            targetTemp: target ?? null,
            isOn: status.switch ?? false,
            mode: mode,
            code: 'switch',
            tempCode: 'temp_set',
            hasThermostat: true,
            hasMode: true, // qn thermostats supportent LOW/HIGH
          });
        });

        // Vannes thermostatiques (wk) - avec mode eco/manual
        devices.filter(d => d.category === 'wk').forEach(d => {
          const status = {};
          (d.status || []).forEach(s => { status[s.code] = s.value; });
          
          let temp = status.temp_current;
          let target = status.temp_set;
          // Les vannes wk utilisent des dixièmes
          if (temp !== undefined) temp = temp / 10;
          if (target !== undefined) target = target / 10;
          
          heaters.push({
            id: d.id,
            name: d.name,
            category: 'wk',
            online: d.online,
            temperature: temp ?? null,
            targetTemp: target ?? null,
            isOn: true, // Les vannes sont toujours "actives"
            mode: status.mode || 'eco',
            hasThermostat: true,
            hasMode: false, // Pas de LOW/HIGH pour les vannes
            tempCode: 'temp_set',
            maxTemp: 21, // Limite à 21°C pour les vannes
          });
        });

        // Chauffage véranda (cz - prise) - récupère la température du capteur Veranda
        devices.filter(d => d.category === 'cz' && d.name?.toLowerCase().includes('chauffage')).forEach(d => {
          const status = {};
          (d.status || []).forEach(s => { status[s.code] = s.value; });
          
          // Associer au capteur de température de la pièce
          const verandaSensor = tempSensors['veranda'];
          
          heaters.push({
            id: d.id,
            name: d.name,
            category: 'cz',
            online: d.online,
            temperature: verandaSensor?.temperature ?? null, // Température du capteur
            targetTemp: null,
            isOn: status.switch_1 ?? status.switch ?? false,
            code: status.switch_1 !== undefined ? 'switch_1' : 'switch',
            hasThermostat: false,
            hasMode: false,
          });
        });

        // Radiateurs SDB (tdq - prises relais) - SAUF Salle de jeu qui est une vanne
        const tdqDeviceIds = new Set();
        devices.filter(d => d.category === 'tdq').forEach(d => {
          const name = d.name?.toLowerCase() || '';
          // Inclure: radiateur sdb UNIQUEMENT (pas salle de jeu)
          if (name.includes('radiateur') && !tdqDeviceIds.has(d.name)) {
            const status = {};
            (d.status || []).forEach(s => { status[s.code] = s.value; });
            tdqDeviceIds.add(d.name);
            
            // Associer au capteur de température de la pièce si disponible
            let roomTemp = null;
            if (name.includes('st louis') || name.includes('gonzague')) {
              // SDB St Louis de Gonzague -> capteur "st louis de gonzague bas"
              roomTemp = tempSensors['st louis de gonzague bas']?.temperature;
            }
            
            heaters.push({
              id: d.id,
              name: d.name,
              category: 'tdq',
              online: d.online,
              temperature: roomTemp,
              targetTemp: null,
              isOn: status.switch_1 ?? status.switch ?? false,
              code: status.switch_1 !== undefined ? 'switch_1' : 'switch',
              hasThermostat: false,
              hasMode: false,
              isElectric: true, // Marqueur pour radiateur électrique
            });
          }
        });

        setHeatingDevices(heaters);

        // === CHAUFFE-EAU ===
        const wh = [];
        
        // dlq = disjoncteur/chauffe-eau avec données électriques
        devices.filter(d => d.category === 'dlq').forEach(d => {
          const status = {};
          (d.status || []).forEach(s => { status[s.code] = s.value; });
          
          // Renommer selon les consignes
          let displayName = d.name;
          const nameLower = d.name?.toLowerCase() || '';
          if (nameLower.includes('cuisine')) {
            displayName = 'EAU CHAUDE CUISINE';
          } else if (nameLower.includes('petite maison')) {
            displayName = 'ECS SDB PETITE MAISON';
          }
          
          wh.push({
            id: d.id,
            name: displayName,
            originalName: d.name,
            online: d.online,
            isOn: status.switch ?? false,
            code: 'switch',
            // Données électriques (scales Tuya: cur_power /10, cur_voltage /10, cur_current en mA, add_ele /1000)
            power: status.cur_power ?? null,
            voltage: status.cur_voltage ?? null,
            current: status.cur_current ?? null,
            energy: status.add_ele ?? null,
            activeTime: d.active_time ?? null, // epoch seconds: date d'installation/activation
          });
        });
        
        // Ajouter un placeholder pour ECS Petite Maison (non connecté)
        wh.push({
          id: 'placeholder-ecs-petite-maison',
          name: 'ECS SDB PETITE MAISON',
          online: false,
          isOn: false,
          isPlaceholder: true,
        });

        setWaterHeaters(wh);

        // === VANNES CHAUFFAGE (pour affichage état) ===
        // On récupère les capteurs de température par pièce
        const roomValves = devices.filter(d => d.category === 'wsdcg').map(d => {
          const status = {};
          (d.status || []).forEach(s => { status[s.code] = s.value; });
          let temp = status.va_temperature;
          if (temp !== undefined && temp > 100) temp = temp / 10;
          return {
            id: d.id,
            name: d.name,
            temperature: temp ?? null,
            online: d.online,
          };
        }).filter(v => v.online && v.temperature !== null);
        setValves(roomValves);
      }

    } catch (e) {
      console.error('Error loading heating data:', e);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    loadData();
    const interval = setInterval(loadData, 300000); // Rafraîchissement toutes les 5 minutes (économie API)
    return () => clearInterval(interval);
  }, [loadData]);

  // Commande température
  const setDeviceTemp = async (device, temp) => {
    try {
      let value = temp;
      // Les vannes wk utilisent des dixièmes, les thermostats qn NON
      if (device.category === 'wk') {
        value = Math.round(temp * 10);
      } else {
        value = Math.round(temp);
      }
      console.log(`Setting ${device.name} to ${temp}° (value: ${value}, category: ${device.category})`);
      await api.post(`/integrations/tuya/devices/${device.id}/command`, {
        code: device.tempCode || 'temp_set', 
        value
      });
      // Mise à jour optimiste de l'UI
      setHeatingDevices(prev => prev.map(d => 
        d.id === device.id ? { ...d, targetTemp: temp } : d
      ));
      setTimeout(loadData, 2000);
    } catch (e) {
      console.error('Error setting temp:', e);
      alert('Erreur: ' + (e.response?.data?.detail || e.message));
    }
  };

  // Commande toggle
  const toggleDevice = async (device) => {
    try {
      const newState = !device.isOn;
      console.log(`Toggling ${device.name} (${device.code}) to ${newState}`);
      
      // Mise à jour optimiste de l'UI
      setHeatingDevices(prev => prev.map(d => 
        d.id === device.id ? { ...d, isOn: newState } : d
      ));
      
      await api.post(`/integrations/tuya/devices/${device.id}/command`, {
        code: device.code, 
        value: newState
      });
      
      // Recharger les données après 2s pour confirmer l'état réel
      setTimeout(loadData, 2000);
    } catch (e) {
      console.error('Error toggling:', e);
      // Annuler la mise à jour optimiste en cas d'erreur
      loadData();
      alert('Erreur: ' + (e.response?.data?.detail || e.message));
    }
  };

  // Commande mode LOW/HIGH pour thermostats qn
  const setDeviceMode = async (device, newMode) => {
    try {
      // LOW = hors-gel (5°C), HIGH = confort (19°C)
      const targetTemp = newMode === 'low' ? 5 : 19;
      
      console.log(`Setting ${device.name} mode to ${newMode} (temp=${targetTemp})`);
      await api.post(`/integrations/tuya/devices/${device.id}/command`, {
        code: device.tempCode || 'temp_set', 
        value: targetTemp
      });
      
      // Mise à jour optimiste
      setHeatingDevices(prev => prev.map(d => 
        d.id === device.id ? { ...d, targetTemp: targetTemp, mode: newMode } : d
      ));
    } catch (e) {
      console.error('Error setting mode:', e);
      alert('Erreur: ' + (e.response?.data?.detail || e.message));
    }
  };

  // Commande mode Viessmann
  const setViessmannMode = async (mode) => {
    try {
      console.log(`Setting Viessmann mode to ${mode}`);
      
      // Mise à jour optimiste
      setVicare(prev => ({
        ...prev,
        heating: { ...prev?.heating, active_mode: mode }
      }));
      
      await api.post('/integrations/vicare/heating/mode', { mode });
      setTimeout(loadData, 3000);
    } catch (e) {
      console.error('Error setting mode:', e);
      alert('Erreur: ' + (e.response?.data?.detail || e.message));
      loadData();
    }
  };

  // ARRÊT GÉNÉRAL - Éteint tous les chauffages d'un coup
  const handleGlobalStop = async () => {
    if (globalActionLoading) return;
    setGlobalActionLoading(true);
    
    try {
      console.log('ARRÊT GÉNÉRAL de tous les chauffages');
      
      // 1. Mettre Viessmann en VEILLE
      if (vicare) {
        await api.post('/integrations/vicare/heating/mode', { mode: 'standby' }).catch(() => {});
      }
      
      // 2. Éteindre le poêle à pellets
      if (pelletStove?.connected) {
        await api.post('/integrations/pellet-stove/power/0').catch(() => {});
      }
      
      // 3. Éteindre tous les radiateurs/vannes Tuya
      const promises = [];
      for (const device of heatingDevices) {
        if (device.online && device.code) {
          promises.push(
            api.post(`/integrations/tuya/devices/${device.id}/command`, {
              code: device.code,
              value: false
            }).catch(() => {})
          );
        }
        // Pour les vannes, mettre à température minimale
        if (device.online && device.hasThermostat && device.category === 'wk') {
          const value = device.category === 'wk' ? 50 : 5; // 5°C (dixièmes pour wk)
          promises.push(
            api.post(`/integrations/tuya/devices/${device.id}/command`, {
              code: device.tempCode || 'temp_set',
              value
            }).catch(() => {})
          );
        }
      }
      
      // 4. Éteindre les chauffe-eau électriques (disjoncteurs ECS)
      for (const device of ecsDevices) {
        if (device.online && device.id) {
          promises.push(
            api.post(`/integrations/tuya/devices/${device.id}/command`, {
              code: 'switch_1',
              value: false
            }).catch(() => {})
          );
        }
      }
      
      await Promise.all(promises);
      
      // Mise à jour optimiste de l'UI
      setVicare(prev => prev ? { ...prev, heating: { ...prev?.heating, active_mode: 'standby' } } : prev);
      setHeatingDevices(prev => prev.map(d => ({ ...d, isOn: false })));
      setPelletStove(prev => prev ? { ...prev, is_on: false } : prev);
      setEcsDevices(prev => prev.map(d => ({ ...d, isOn: false })));
      
      setTimeout(loadData, 2000);
    } catch (e) {
      console.error('Error in global stop:', e);
      alert('Erreur: ' + (e.response?.data?.detail || e.message));
    } finally {
      setGlobalActionLoading(false);
    }
  };

  // MARCHE GÉNÉRAL - Allume tous les chauffages
  const handleGlobalStart = async () => {
    if (globalActionLoading) return;
    setGlobalActionLoading(true);
    
    try {
      console.log('MARCHE GÉNÉRAL de tous les chauffages');
      
      // 1. Mettre Viessmann en mode CH+ECS
      if (vicare) {
        await api.post('/integrations/vicare/heating/mode', { mode: 'dhwAndHeating' }).catch(() => {});
      }
      
      // 2. Allumer le poêle à pellets (puissance 3 = moyenne)
      if (pelletStove?.connected) {
        await api.post('/integrations/pellet-stove/power/3').catch(() => {});
      }
      
      // 3. Allumer tous les radiateurs/vannes Tuya
      const promises = [];
      for (const device of heatingDevices) {
        if (device.online && device.code) {
          promises.push(
            api.post(`/integrations/tuya/devices/${device.id}/command`, {
              code: device.code,
              value: true
            }).catch(() => {})
          );
        }
        // Pour les vannes, mettre à température confort (19°C)
        if (device.online && device.hasThermostat && device.category === 'wk') {
          const value = device.category === 'wk' ? 190 : 19; // 19°C (dixièmes pour wk)
          promises.push(
            api.post(`/integrations/tuya/devices/${device.id}/command`, {
              code: device.tempCode || 'temp_set',
              value
            }).catch(() => {})
          );
        }
      }
      
      // 4. Allumer les chauffe-eau électriques (disjoncteurs ECS)
      for (const device of ecsDevices) {
        if (device.online && device.id) {
          promises.push(
            api.post(`/integrations/tuya/devices/${device.id}/command`, {
              code: 'switch_1',
              value: true
            }).catch(() => {})
          );
        }
      }
      
      await Promise.all(promises);
      
      // Mise à jour optimiste de l'UI
      setVicare(prev => prev ? { ...prev, heating: { ...prev?.heating, active_mode: 'dhwAndHeating' } } : prev);
      setHeatingDevices(prev => prev.map(d => ({ ...d, isOn: true })));
      setPelletStove(prev => prev ? { ...prev, is_on: true } : prev);
      setEcsDevices(prev => prev.map(d => ({ ...d, isOn: true })));
      
      setTimeout(loadData, 2000);
    } catch (e) {
      console.error('Error in global start:', e);
      alert('Erreur: ' + (e.response?.data?.detail || e.message));
    } finally {
      setGlobalActionLoading(false);
    }
  };

  // Ajuster température cible Viessmann
  const adjustViessmannTemp = async (newTemp) => {
    try {
      const finalTemp = Math.max(10, Math.min(21, Math.round(newTemp)));
      
      console.log(`Setting Viessmann heating temp to ${finalTemp}°C`);
      
      // Mise à jour optimiste
      setVicare(prev => ({
        ...prev,
        heating: { ...prev?.heating, target_temperature: finalTemp }
      }));
      
      await api.post('/integrations/vicare/heating/temperature', { temperature: finalTemp });
      setTimeout(loadData, 3000);
    } catch (e) {
      console.error('Error setting Viessmann temp:', e);
      alert('Erreur: ' + (e.response?.data?.detail || e.message));
      loadData();
    }
  };

  // Ajuster courbe de chauffe
  const adjustCurve = async (param, delta) => {
    try {
      const currentSlope = vicare?.heating?.curve?.slope ?? 1.5;
      const currentShift = vicare?.heating?.curve?.shift ?? 0;
      
      let newSlope = currentSlope;
      let newShift = currentShift;
      
      if (param === 'slope') {
        newSlope = Math.max(0.2, Math.min(3.5, currentSlope + delta));
        newSlope = Math.round(newSlope * 10) / 10;
      } else {
        newShift = Math.max(-13, Math.min(40, currentShift + delta));
        newShift = Math.round(newShift);
      }
      
      console.log(`Adjusting heating curve: slope=${newSlope}, shift=${newShift}`);
      
      // Mise à jour optimiste
      setVicare(prev => ({
        ...prev,
        heating: { ...prev?.heating, curve: { slope: newSlope, shift: newShift } }
      }));
      
      await api.post('/integrations/vicare/heating/curve', { 
        slope: newSlope, 
        shift: newShift 
      });
      setTimeout(loadData, 3000);
    } catch (e) {
      console.error('Error adjusting curve:', e);
      alert('Erreur: ' + (e.response?.data?.detail || e.message));
      loadData();
    }
  };

  // Données ViCare
  const boilerTemp = vicare?.temperatures?.boiler ?? null;
  const dhwTemp = vicare?.temperatures?.dhw ?? null;
  const outsideTemp = vicare?.temperatures?.outside ?? null;
  const burnerActive = vicare?.burner?.active ?? false;
  const heatingMode = vicare?.heating?.active_mode || 'standby';
  const dhwTarget = vicare?.dhw?.target_temperature ?? 50;
  const heatingTarget = vicare?.heating?.target_temperature ?? 20;

  const getModeLabel = (mode) => ({
    'dhwAndHeating': 'CH + ECS',
    'dhw': 'ECS SEUL',
    'heating': 'CHAUFF.',
    'standby': 'VEILLE',
    'forcedReduced': 'RÉDUIT',
  }[mode] || 'VEILLE');

  if (loading) {
    return (
      <Layout title="">
        <div className="h-full flex items-center justify-center">
          <div className="text-cyan-400 font-mono animate-pulse">CHARGEMENT...</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout title="">
      <div className="h-full flex flex-col overflow-hidden" style={{ background: '#030303' }}>
        {/* Header compact */}
        <div className="flex items-center justify-between px-3 py-2 border-b border-orange-500/30 bg-orange-950/20">
          <div className="flex items-center gap-2">
            <Flame className="w-5 h-5 text-orange-500" />
            <span className="font-mono font-bold text-orange-400">CHAUFFAGE & ECS</span>
          </div>
          <button
            onClick={() => { setRefreshing(true); loadData(); }}
            className="p-1.5 rounded bg-gray-800/50 hover:bg-gray-700"
          >
            <RefreshCw className={`w-4 h-4 text-cyan-400 ${refreshing ? 'animate-spin' : ''}`} />
          </button>
        </div>

        {/* Main Grid - 3 colonnes */}
        <div className="flex-1 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 p-2 overflow-hidden">
          
          {/* COL 1: Chaudière + Poêle */}
          <div className="space-y-3">
            {/* CHAUDIÈRE VIESSMANN - Layout 2 colonnes */}
            <div className="p-2 rounded-xl border border-red-500/40 bg-gradient-to-br from-red-950/30 to-transparent">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-lg" style={{ filter: 'hue-rotate(180deg) saturate(2)' }}>🔥</span>
                <img 
                  src={LOGOS.viessmann} 
                  alt="Viessmann" 
                  className="h-5 brightness-200"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
                <span className="font-mono font-bold text-red-400 text-sm">CHAUDIÈRE VIESSMANN</span>
              </div>

              {vicare ? (
                <div className="grid grid-cols-2 gap-2">
                  {/* GAUCHE: Dial + Températures + Modes */}
                  <div>
                    <div className="flex justify-center">
                      <HeaterDial
                        name=""
                        temperature={boilerTemp}
                        targetTemp={heatingTarget}
                        isOn={burnerActive}
                        online={true}
                        onSetTemp={adjustViessmannTemp}
                        minTemp={10}
                        maxTemp={21}
                      />
                    </div>

                    <div className="grid grid-cols-3 gap-1 mt-2 text-center">
                      <div className="p-1 rounded bg-black/40 border border-gray-800">
                        <div className="text-[8px] text-gray-500">EXT.</div>
                        <div className="text-xs font-mono text-cyan-400">{outsideTemp?.toFixed(1) ?? '--'}°</div>
                      </div>
                      <div className="p-1 rounded bg-black/40 border border-gray-800">
                        <div className="text-[8px] text-gray-500">CIRCUIT</div>
                        <div className="text-xs font-mono text-orange-400">{boilerTemp?.toFixed(1) ?? '--'}°</div>
                      </div>
                      <div className="p-1 rounded bg-black/40 border border-gray-800">
                        <div className="text-[8px] text-gray-500">ECS</div>
                        <div className="text-xs font-mono text-blue-400">{dhwTemp?.toFixed(1) ?? '--'}°</div>
                      </div>
                    </div>

                    {/* MODES */}
                    <div className="mt-2">
                      <div className="text-[8px] text-gray-500 mb-1 font-mono">MODE</div>
                      <div className="grid grid-cols-4 gap-0.5">
                        {[
                          { mode: 'standby', label: 'ARRÊT' },
                          { mode: 'dhw', label: 'ECS' },
                          { mode: 'dhwAndHeating', label: 'CH+ECS' },
                          { mode: 'forcedReduced', label: 'RÉDUIT' },
                        ].map(({ mode, label }) => (
                          <button
                            key={mode}
                            onClick={() => setViessmannMode(mode)}
                            className={`py-1 px-0.5 rounded text-[8px] font-mono font-bold transition-all ${
                              heatingMode === mode 
                                ? 'bg-orange-500 text-white shadow-lg shadow-orange-500/30' 
                                : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                            }`}
                          >
                            {label}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* DROITE: Courbe de chauffe + Brûleur + Conso gaz chauffage */}
                  <div className="space-y-2">
                    {/* COURBE DE CHAUFFE */}
                    <div className="p-2 rounded bg-black/40 border border-gray-800">
                      <div className="text-[8px] text-gray-500 mb-1.5 font-mono">COURBE DE CHAUFFE</div>
                      <div className="grid grid-cols-2 gap-2">
                        <div className="text-center">
                          <div className="flex items-center justify-center gap-1">
                            <button 
                              onClick={() => adjustCurve('slope', -0.1)}
                              className="w-5 h-5 rounded bg-gray-700 hover:bg-gray-600 flex items-center justify-center active:scale-90"
                            >
                              <Minus className="w-2.5 h-2.5 text-white" />
                            </button>
                            <div className="text-base font-mono font-bold text-cyan-400 min-w-[35px]">
                              {vicare?.heating?.curve?.slope?.toFixed(1) ?? '--'}
                            </div>
                            <button 
                              onClick={() => adjustCurve('slope', 0.1)}
                              className="w-5 h-5 rounded bg-gray-700 hover:bg-gray-600 flex items-center justify-center active:scale-90"
                            >
                              <Plus className="w-2.5 h-2.5 text-white" />
                            </button>
                          </div>
                          <div className="text-[8px] text-gray-500">PENTE</div>
                        </div>
                        <div className="text-center">
                          <div className="flex items-center justify-center gap-1">
                            <button 
                              onClick={() => adjustCurve('shift', -1)}
                              className="w-5 h-5 rounded bg-gray-700 hover:bg-gray-600 flex items-center justify-center active:scale-90"
                            >
                              <Minus className="w-2.5 h-2.5 text-white" />
                            </button>
                            <div className="text-base font-mono font-bold text-cyan-400 min-w-[35px]">
                              {vicare?.heating?.curve?.shift ?? '--'}
                            </div>
                            <button 
                              onClick={() => adjustCurve('shift', 1)}
                              className="w-5 h-5 rounded bg-gray-700 hover:bg-gray-600 flex items-center justify-center active:scale-90"
                            >
                              <Plus className="w-2.5 h-2.5 text-white" />
                            </button>
                          </div>
                          <div className="text-[8px] text-gray-500">DÉCALAGE</div>
                        </div>
                      </div>
                    </div>

                    {/* BRÛLEUR STATUS */}
                    <div className={`p-1.5 rounded text-center text-[10px] font-mono ${
                      burnerActive ? 'bg-red-500/30 text-red-300' : 'bg-gray-800/50 text-gray-500'
                    }`}>
                      {burnerActive ? '🔥 BRÛLEUR ACTIF' : '○ VEILLE'}
                      <div className="text-[9px] text-gray-500">
                        {Math.round(vicare?.burner?.hours ?? 0)}h / {vicare?.burner?.starts ?? 0} dém.
                      </div>
                    </div>

                    {/* CONSO GAZ CHAUFFAGE - Style comme chauffe-eau */}
                    <GasConsumptionCard 
                      title="GAZ CHAUFFAGE"
                      data={vicare?.gas_consumption?.heating}
                      pricePerKwh={0.16}
                      color="orange"
                    />
                  </div>
                </div>
              ) : (
                <div className="text-center py-6 text-red-400/60 font-mono text-sm">
                  <AlertTriangle className="w-6 h-6 mx-auto mb-1" />
                  VICARE DÉCONNECTÉ
                </div>
              )}
            </div>

            {/* POÊLE À PELLETS - Dynamique */}
            <div className="p-2 rounded-xl border border-orange-600/40 bg-gradient-to-br from-orange-950/30 to-transparent">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-lg">🪵</span>
                <span className="text-lg">{LOGOS.italia}</span>
                <span className="font-mono font-bold text-orange-400 text-sm">POÊLE À PELLETS</span>
                {pelletStove?.connected && (
                  <span className="text-[8px] font-mono text-green-400 ml-auto">CONNECTÉ</span>
                )}
              </div>

              <div className="grid grid-cols-2 gap-3">
                {/* GAUCHE: Dial + État */}
                <div>
                  <div className="flex justify-center">
                    <HeaterDial
                      name=""
                      temperature={pelletStove?.ambient_temp ?? null}
                      targetTemp={pelletStove?.target_temp ?? null}
                      isOn={pelletStove?.is_on ?? false}
                      online={pelletStove?.connected ?? false}
                      onSetTemp={pelletStove?.connected ? async (temp) => {
                        try {
                          await api.post(`/integrations/pellet-stove/temperature/${Math.min(temp, 21)}`);
                          setPelletStove(prev => ({ ...prev, target_temp: Math.min(temp, 21) }));
                        } catch (e) { console.error(e); }
                      } : undefined}
                      minTemp={10}
                      maxTemp={21}
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-1 mt-2 text-center">
                    <div className="p-1 rounded bg-black/40 border border-gray-800">
                      <div className="text-[8px] text-gray-500">CIBLE</div>
                      <div className="text-xs font-mono text-orange-400">{pelletStove?.target_temp ? `${pelletStove.target_temp}°` : '--°'}</div>
                    </div>
                    <div className="p-1 rounded bg-black/40 border border-gray-800">
                      <div className="text-[8px] text-gray-500">FUMÉES</div>
                      <div className="text-xs font-mono text-red-400">{pelletStove?.gas_temp ? `${pelletStove.gas_temp}°` : '--°'}</div>
                    </div>
                  </div>

                  {/* ÉTAT DU POÊLE */}
                  <div className="mt-2">
                    <div className="text-[8px] text-gray-500 mb-1 font-mono">ÉTAT</div>
                    
                    {/* Alerte Pellets Épuisés */}
                    {pelletStove?.status_code === 8 && (
                      <div className="mb-2 p-1.5 rounded bg-red-900/50 border border-red-500 animate-pulse">
                        <div className="text-[10px] font-mono font-bold text-red-400 text-center">
                          ⚠️ PELLETS ÉPUISÉS - RECHARGER
                        </div>
                      </div>
                    )}
                    
                    {/* Alerte Défaut */}
                    {pelletStove?.status_code === 7 && (
                      <div className="mb-2 p-1.5 rounded bg-yellow-900/50 border border-yellow-500">
                        <div className="text-[10px] font-mono font-bold text-yellow-400 text-center">
                          ⚠️ DÉFAUT DÉTECTÉ
                        </div>
                      </div>
                    )}
                    
                    <div className="grid grid-cols-4 gap-0.5">
                      {[
                        { state: 'off', label: 'ARRÊT', codes: [0] },
                        { state: 'ignition', label: 'ALLUM.', codes: [1, 2, 3] },
                        { state: 'running', label: 'MARCHE', codes: [4] },
                        { state: 'cleaning', label: 'NETT.', codes: [5, 6] },
                      ].map(({ state, label, codes }) => {
                        const isActive = pelletStove?.connected && codes.includes(pelletStove?.status_code);
                        return (
                          <div
                            key={state}
                            className={`py-1 px-0.5 rounded text-[8px] font-mono font-bold text-center ${
                              isActive ? 'bg-orange-500 text-white' : 'bg-gray-800 text-gray-500'
                            }`}
                          >
                            {label}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>

                {/* DROITE: Puissance + ON/OFF */}
                <div className="space-y-2">
                  {/* PUISSANCE 1-5 */}
                  <div className="p-2 rounded bg-black/40 border border-gray-800">
                    <div className="text-[8px] text-gray-500 mb-1.5 font-mono">PUISSANCE</div>
                    <div className="flex items-center justify-center gap-1">
                      {[1, 2, 3, 4, 5].map(level => (
                        <button
                          key={level}
                          disabled={!pelletStove?.connected}
                          onClick={async () => {
                            try {
                              await api.post(`/integrations/pellet-stove/power/${level}`);
                              setPelletStove(prev => ({ ...prev, set_power_level: level }));
                            } catch (e) { console.error(e); }
                          }}
                          className={`w-6 h-6 rounded text-[10px] font-mono font-bold transition-all ${
                            (pelletStove?.power_level || pelletStove?.set_power_level) === level
                              ? 'bg-orange-500 text-white'
                              : pelletStove?.connected
                                ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                                : 'bg-gray-700 text-gray-500 opacity-50'
                          }`}
                        >
                          {level}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* ON/OFF */}
                  <div className="flex justify-center gap-2">
                    <button
                      disabled={!pelletStove?.connected}
                      onClick={async () => {
                        try {
                          await api.post('/integrations/pellet-stove/on');
                          setPelletStove(prev => ({ ...prev, is_on: true }));
                          setTimeout(loadData, 3000);
                        } catch (e) { console.error(e); }
                      }}
                      className={`px-4 py-2 rounded text-[10px] font-mono font-bold transition-all ${
                        pelletStove?.is_on
                          ? 'bg-orange-500 text-white shadow-lg shadow-orange-500/30'
                          : pelletStove?.connected
                            ? 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                            : 'bg-gray-800 text-gray-500 opacity-50'
                      }`}
                    >
                      MARCHE
                    </button>
                    <button
                      disabled={!pelletStove?.connected}
                      onClick={async () => {
                        try {
                          await api.post('/integrations/pellet-stove/off');
                          setPelletStove(prev => ({ ...prev, is_on: false }));
                          setTimeout(loadData, 3000);
                        } catch (e) { console.error(e); }
                      }}
                      className={`px-4 py-2 rounded text-[10px] font-mono font-bold transition-all ${
                        !pelletStove?.is_on && pelletStove?.connected
                          ? 'bg-red-500 text-white'
                          : pelletStove?.connected
                            ? 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                            : 'bg-gray-800 text-gray-500 opacity-50'
                      }`}
                    >
                      ARRÊT
                    </button>
                  </div>

                  {/* Info connexion */}
                  <div className={`p-2 rounded border text-center ${
                    pelletStove?.connected
                      ? 'bg-green-900/20 border-green-700/30'
                      : 'bg-orange-900/20 border-orange-700/30'
                  }`}>
                    <div className={`text-[10px] font-mono ${pelletStove?.connected ? 'text-green-400/70' : 'text-orange-400/70'}`}>
                      {pelletStove?.connected ? (pelletStove?.status_text || 'CONNECTÉ') : 'HORS LIGNE'}
                    </div>
                    <div className={`text-[8px] ${pelletStove?.connected ? 'text-green-400/50' : 'text-orange-400/50'}`}>
                      {pelletStove?.connected ? pelletStove?.name || 'Stufe a Pellet Italia' : 'En attente de connexion'}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* TOGGLE ARRÊT/MARCHE GÉNÉRAL */}
            <div className="relative">
              <div className="flex flex-col gap-0 rounded-xl overflow-hidden border border-gray-700">
                {/* Bouton MARCHE */}
                <button
                  onClick={handleGlobalStart}
                  disabled={globalActionLoading}
                  className={`py-3 px-4 font-bold text-sm uppercase tracking-wide transition-all duration-300 ${
                    globalActionLoading 
                      ? 'bg-gray-700 text-gray-400 cursor-not-allowed'
                      : 'bg-gradient-to-r from-green-700 to-green-600 hover:from-green-600 hover:to-green-500 text-white active:scale-[0.98]'
                  }`}
                  style={{ fontFamily: 'Impact, sans-serif', letterSpacing: '2px' }}
                >
                  {globalActionLoading ? '...' : '▲ MARCHE GÉNÉRAL'}
                </button>
                
                {/* Séparateur avec indicateur */}
                <div className="h-1 bg-gray-900 relative">
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-8 h-3 rounded-full bg-gray-800 border border-gray-600 flex items-center justify-center">
                      <div className="w-2 h-2 rounded-full bg-cyan-400 animate-pulse"></div>
                    </div>
                  </div>
                </div>
                
                {/* Bouton ARRÊT */}
                <button
                  onClick={handleGlobalStop}
                  disabled={globalActionLoading}
                  className={`py-3 px-4 font-bold text-sm uppercase tracking-wide transition-all duration-300 ${
                    globalActionLoading 
                      ? 'bg-gray-700 text-gray-400 cursor-not-allowed'
                      : 'bg-gradient-to-r from-red-700 to-red-600 hover:from-red-600 hover:to-red-500 text-white active:scale-[0.98]'
                  }`}
                  style={{ fontFamily: 'Impact, sans-serif', letterSpacing: '2px' }}
                >
                  {globalActionLoading ? '...' : '▼ ARRÊT GÉNÉRAL'}
                </button>
              </div>
            </div>
          </div>

          {/* COL 2: Radiateurs */}
          <div className="space-y-2">
            {/* VANNES CHAUFFAGE CENTRAL (GAZ) */}
            <div className="p-2 rounded-lg border border-orange-500/30 bg-gradient-to-br from-orange-950/20 to-transparent">
              <div className="flex items-center gap-1 mb-2">
                <span className="text-base" style={{ filter: 'hue-rotate(180deg) saturate(2)' }}>🔥</span>
                <span className="font-mono font-bold text-orange-400 text-[10px]">
                  VANNES CHAUFFAGE CENTRAL ({heatingDevices.filter(d => d.category === 'wk').length + 1})
                </span>
              </div>
              <div className="grid grid-cols-2 gap-2">
                {heatingDevices.filter(d => d.category === 'wk').filter(d => {
                  // Éviter doublon Salle de jeu si déjà dans les données
                  const name = (d.name || '').toLowerCase();
                  return !name.includes('salle de jeu');
                }).map(device => (
                  <HeaterDial
                    key={device.id}
                    name={device.name.replace(/^Vanne\s*/i, '').replace(/^Chauffage\s*/i, '')}
                    temperature={device.temperature}
                    targetTemp={device.targetTemp}
                    isOn={device.isOn}
                    online={device.online}
                    onSetTemp={device.hasThermostat ? (temp) => setDeviceTemp(device, temp) : undefined}
                    minTemp={5}
                    maxTemp={device.maxTemp ?? 20}
                  />
                ))}
                {/* Salle de jeu - vanne gaz */}
                <HeaterDial
                  name="SALLE DE JEU"
                  temperature={null}
                  targetTemp={null}
                  isOn={false}
                  online={false}
                />
              </div>
            </div>

            {/* RADIATEURS ÉLECTRIQUES */}
            <div className="p-2 rounded-lg border border-cyan-500/30 bg-gradient-to-br from-cyan-950/20 to-transparent">
              <div className="flex items-center gap-1 mb-2">
                <span className="text-base">⚡</span>
                <span className="font-mono font-bold text-cyan-400 text-[10px]">
                  RADIATEURS ÉLECTRIQUES ({heatingDevices.filter(d => d.category !== 'wk').length + 2})
                </span>
              </div>
              <div className="grid grid-cols-3 gap-1">
                {heatingDevices.filter(d => d.category !== 'wk').map(device => {
                  // Renommer "Radiateur SDB St Louis de Gonzague" pour afficher DE à la ligne avec GONZAGUE
                  let displayName = device.name.replace(/^Chauffage\s*/i, '').replace(/^Radiateur\s*/i, '');
                  if (displayName.toLowerCase().includes('gonzague')) {
                    displayName = 'SDB ST LOUIS\nDE GONZAGUE';
                  }
                  return (
                    <HeaterDial
                      key={device.id}
                      name={displayName}
                      temperature={device.temperature}
                      targetTemp={device.targetTemp}
                      isOn={device.isOn}
                      online={device.online}
                      onSetTemp={device.hasThermostat ? (temp) => setDeviceTemp(device, temp) : undefined}
                      onToggle={device.code ? () => toggleDevice(device) : undefined}
                      minTemp={5}
                      maxTemp={21}
                    />
                  );
                })}
                {/* Placeholder CHAUFFE SERVIETTE JÉRUSALEM */}
                <HeaterDial
                  name="CHAUFFE SERVIETTE JÉRUSALEM"
                  temperature={null}
                  targetTemp={null}
                  isOn={false}
                  online={false}
                />
                {/* PAC Olimpia Splendid - Salle de jeu */}
                <HeaterDial
                  name="PAC SALLE DE JEU"
                  subtitle="OLIMPIA SPLENDID"
                  temperature={null}
                  targetTemp={null}
                  isOn={false}
                  online={false}
                />
              </div>
            </div>
          </div>

          {/* COL 3: ECS */}
          <div className="space-y-2 overflow-hidden">
            {/* ECS GAZ VIESSMANN */}
            {vicare && (
              <div className="p-1.5 rounded-lg border border-blue-500/30 bg-gradient-to-br from-blue-950/20 to-transparent">
                <div className="flex items-center gap-1 mb-1">
                  <span className="text-sm" style={{ filter: 'hue-rotate(180deg) saturate(2)' }}>🔥</span>
                  <span className="font-mono font-bold text-blue-400 text-[9px]">ECS TOUTES SDB SAUF PETITE MAISON</span>
                </div>
                <div className="flex items-center justify-around py-0.5">
                  <div className="text-center">
                    <div className="text-base font-mono font-bold text-blue-400">{Math.round(dhwTemp ?? 0)}°</div>
                    <div className="text-[6px] text-gray-500">ACTUEL</div>
                  </div>
                  <div className="text-sm text-gray-600">→</div>
                  <div className="text-center">
                    <div className="text-base font-mono font-bold text-cyan-400">{dhwTarget}°</div>
                    <div className="text-[6px] text-gray-500">CIBLE</div>
                  </div>
                </div>
                <GasConsumptionCard 
                  title=""
                  data={vicare?.gas_consumption?.dhw}
                  pricePerKwh={0.16}
                  color="blue"
                />
              </div>
            )}

            {/* Chauffe-eau thermodynamique Ariston - Cuisine */}
            <div className="p-1.5 rounded-lg border border-green-500/30 bg-gradient-to-br from-green-950/20 to-transparent">
              <div className="flex items-center gap-1 mb-0.5">
                <span className="text-sm">🌡️</span>
                <span className="font-mono font-bold text-green-400 text-[9px]">ECS CUISINE (THERMODYNAMIQUE)</span>
              </div>
              <div className="flex items-center justify-between p-1.5 rounded bg-black/30 border border-green-500/20">
                <div>
                  <div className="font-mono text-green-400 text-[10px] font-bold">Chauffe-eau Ariston</div>
                  <div className="text-[7px] text-gray-500">THERMODYNAMIQUE</div>
                </div>
                <div className="flex items-center gap-1">
                  <span className="text-[8px] text-yellow-500 font-mono">NON CONNECTÉ</span>
                  <div className="w-1.5 h-1.5 rounded-full bg-yellow-500/50 animate-pulse" />
                </div>
              </div>
            </div>

            {/* Chauffe-eau électriques */}
            <div className="p-1.5 rounded-lg border border-yellow-500/30 bg-gradient-to-br from-yellow-950/20 to-transparent">
              <div className="flex items-center gap-1 mb-0.5">
                <span className="text-sm">⚡</span>
                <span className="font-mono font-bold text-yellow-400 text-[9px]">CHAUFFE-EAU ÉLECTRIQUES</span>
              </div>
              <div className="space-y-0.5">
                {waterHeaters.map(wh => (
                  <WaterHeaterCard 
                    key={wh.id}
                    wh={wh}
                    onToggle={() => toggleDevice(wh)}
                  />
                ))}
              </div>
            </div>
            
            {/* TOTAL ECS - Gaz + Électrique */}
            <TotalECSCard 
              gasData={vicare?.gas_consumption?.dhw}
              electricData={waterHeaters}
            />
          </div>
        </div>
      </div>
    </Layout>
  );
};

/**
 * Carte Chauffe-eau électrique avec données réelles (sans extrapolation bidon).
 * Affiche : statut, puissance/tension/intensité instantanées, cumul kWh depuis installation,
 * durée écoulée et moyenne/jour calculée.
 */
const WaterHeaterCard = ({ wh, onToggle }) => {
  // Scales Tuya officiels (spec /v1.0/iot-03/devices/{id}/specification)
  // add_ele scale=3 -> kWh = value/1000
  // cur_power scale=1 -> W = value/10
  // cur_voltage scale=1 -> V = value/10
  // cur_current unit=mA, no scale
  const totalKwh = (wh.energy !== null && wh.energy !== undefined) ? wh.energy / 1000 : null;
  const watts = (wh.power !== null && wh.power !== undefined) ? wh.power / 10 : null;
  const volts = (wh.voltage !== null && wh.voltage !== undefined) ? wh.voltage / 10 : null;
  const amps = (wh.current !== null && wh.current !== undefined) ? wh.current / 1000 : null;
  
  const pricePerKwh = 0.1883; // €/kWh Total Énergies
  
  // Durée écoulée depuis installation (active_time epoch seconds)
  let durationLabel = null;
  let installLabel = null;
  let avgPerDay = null;
  if (wh.activeTime) {
    const installDate = new Date(wh.activeTime * 1000);
    installLabel = installDate.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
    const elapsedMs = Date.now() - installDate.getTime();
    const days = elapsedMs / (1000 * 60 * 60 * 24);
    if (days < 1) {
      const hours = elapsedMs / (1000 * 60 * 60);
      durationLabel = hours < 1 ? `${Math.round(elapsedMs / 60000)} min` : `${hours.toFixed(1)} h`;
    } else {
      durationLabel = days < 30 ? `${Math.floor(days)} j` : `${Math.floor(days / 30)} mois`;
    }
    if (days >= 0.05 && totalKwh !== null) {
      avgPerDay = totalKwh / Math.max(days, 0.05);
    }
  }
  
  return (
    <div className={`p-2 rounded border ${wh.isPlaceholder ? 'bg-gray-900/40 border-gray-700 border-dashed' : 'bg-black/40 border-gray-800'}`} data-testid={`water-heater-${wh.id || 'placeholder'}`}>
      <div className="flex items-center justify-between mb-1">
        <div>
          <div className="font-mono text-white text-xs font-bold">{wh.name}</div>
          <div className={`text-[9px] font-mono ${wh.isPlaceholder ? 'text-gray-500' : (wh.online ? 'text-green-400' : 'text-red-400')}`}>
            {wh.isPlaceholder ? 'ATTENTE CONNEXION' : (wh.online ? 'OK' : 'Hors ligne')}
          </div>
        </div>
        {!wh.isPlaceholder && wh.online && (
          <IndustrialSwitch isOn={wh.isOn} onChange={onToggle} disabled={!wh.online} />
        )}
        {wh.isPlaceholder && <div className="text-lg text-gray-600">--</div>}
      </div>
      
      {/* Données électriques live */}
      <div className="flex items-center gap-2 py-1 border-t border-gray-700/50 text-[10px] font-mono">
        <span className={wh.isPlaceholder ? 'text-gray-600' : 'text-yellow-400'}>
          ⚡{wh.isPlaceholder || watts === null ? '--' : Math.round(watts)}W
        </span>
        <span className={wh.isPlaceholder ? 'text-gray-600' : 'text-gray-400'}>
          {wh.isPlaceholder || volts === null ? '--' : Math.round(volts)}V
        </span>
        <span className={wh.isPlaceholder ? 'text-gray-600' : 'text-gray-400'}>
          {wh.isPlaceholder || amps === null ? '--' : amps.toFixed(2)}A
        </span>
      </div>
      
      {/* Cumul réel depuis installation */}
      {!wh.isPlaceholder && totalKwh !== null && (
        <div className="mt-1 p-1.5 rounded bg-gray-900/50 border border-gray-800">
          <div className="flex items-center justify-between text-[8px] font-mono text-gray-500 uppercase">
            <span>Cumul depuis {installLabel || 'installation'}</span>
            {durationLabel && <span className="text-cyan-400">{durationLabel}</span>}
          </div>
          <div className="flex items-baseline gap-3 mt-0.5">
            <span className="text-base font-mono font-bold text-cyan-400">
              {totalKwh.toFixed(2)} <span className="text-[10px]">kWh</span>
            </span>
            <span className="text-sm font-mono text-green-400">
              {(totalKwh * pricePerKwh).toFixed(2)}€
            </span>
            {avgPerDay !== null && (
              <span className="text-[9px] font-mono text-gray-500 ml-auto">
                ~{avgPerDay.toFixed(2)} kWh/j
              </span>
            )}
          </div>
        </div>
      )}
      {wh.isPlaceholder && (
        <div className="mt-1 p-1.5 rounded bg-gray-900/50 border border-gray-800 text-[9px] font-mono text-gray-600 text-center">
          Données indisponibles
        </div>
      )}
    </div>
  );
};

/**
 * Total ECS — Gaz (Viessmann) + Électrique (chauffe-eau Tuya).
 * Utilise les vraies données ViCare (jour/semaine/mois/année) et le cumul réel des chauffe-eau électriques.
 */
const TotalECSCard = ({ gasData, electricData }) => {
  const [periodIndex, setPeriodIndex] = useState(2); // par défaut: MOIS
  const periods = ['today', 'week', 'month', 'year'];
  const periodLabels = ['JOUR', 'SEMAINE', 'MOIS', 'ANNÉE'];
  
  const priceGas = 0.16;
  const priceElec = 0.1883;
  
  // Conso électrique cumulée totale depuis installation (vraie valeur)
  let elecCumulKwh = 0;
  let elecActiveTime = null;
  (electricData || []).forEach(wh => {
    if (!wh.isPlaceholder && wh.energy !== null && wh.energy !== undefined) {
      elecCumulKwh += wh.energy / 1000; // scale=3
      if (wh.activeTime && (!elecActiveTime || wh.activeTime < elecActiveTime)) {
        elecActiveTime = wh.activeTime;
      }
    }
  });
  
  // Durée écoulée depuis installation pour calcul moyenne
  let elecDurationDays = null;
  if (elecActiveTime) {
    elecDurationDays = (Date.now() / 1000 - elecActiveTime) / 86400;
  }
  
  // Périodisation de la conso électrique : on calcule au prorata de la durée d'installation
  const getElecForPeriod = () => {
    if (!elecDurationDays || elecDurationDays <= 0) return 0;
    const dayAvg = elecCumulKwh / elecDurationDays;
    switch (periodIndex) {
      case 0: return dayAvg;
      case 1: return Math.min(dayAvg * 7, elecCumulKwh);
      case 2: return Math.min(dayAvg * 30, elecCumulKwh);
      case 3: return dayAvg * 365;
      default: return elecCumulKwh;
    }
  };
  
  const currentPeriod = periods[periodIndex];
  const gasValue = gasData?.[currentPeriod] ?? 0;
  const elecValue = getElecForPeriod();
  const totalKwh = gasValue + elecValue;
  const totalEuro = (gasValue * priceGas) + (elecValue * priceElec);
  
  const installInfo = elecActiveTime
    ? `Élec: ${elecCumulKwh.toFixed(2)} kWh cumulés sur ${elecDurationDays < 1 ? `${(elecDurationDays * 24).toFixed(1)} h` : `${Math.floor(elecDurationDays)} j`}`
    : 'Élec: pas de données';
  
  return (
    <div className="p-2 rounded-lg border border-cyan-500/30 bg-gradient-to-br from-cyan-950/20 to-transparent" data-testid="total-ecs-card">
      <div className="flex items-center justify-between mb-1">
        <span className="font-mono font-bold text-cyan-400 text-[9px]">TOTAL PRODUCTION ECS GAZ + ÉLECTRICITÉ</span>
      </div>
      
      {/* Sélecteur de période (cliquable, plus de défilement auto) */}
      <div className="flex gap-1 mb-1">
        {periodLabels.map((lbl, i) => (
          <button
            key={lbl}
            onClick={() => setPeriodIndex(i)}
            className={`flex-1 py-0.5 text-[8px] font-mono rounded ${
              i === periodIndex ? 'bg-cyan-600 text-white' : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
            }`}
            data-testid={`ecs-period-${lbl.toLowerCase()}`}
          >{lbl}</button>
        ))}
      </div>
      
      <div className="grid grid-cols-2 gap-1 text-[10px] font-mono mb-1">
        <div className="p-1 rounded bg-black/40">
          <div className="text-[7px] text-gray-500">🔥 GAZ (Viessmann)</div>
          <div className="text-blue-400">{gasValue.toFixed(1)} <span className="text-[8px]">kWh</span></div>
        </div>
        <div className="p-1 rounded bg-black/40">
          <div className="text-[7px] text-gray-500">⚡ ÉLEC (chauffe-eau)</div>
          <div className="text-yellow-400">{elecValue.toFixed(2)} <span className="text-[8px]">kWh</span></div>
        </div>
      </div>
      
      <div className="p-1.5 rounded bg-cyan-900/30 border border-cyan-500/30 text-center">
        <div className="text-base font-mono font-bold text-cyan-400">
          {totalKwh.toFixed(1)} <span className="text-[10px]">kWh</span>
        </div>
        <div className="text-sm font-mono text-green-400">
          {totalEuro.toFixed(2)}€
        </div>
      </div>
      
      <div className="mt-1 text-[7px] font-mono text-gray-500 leading-tight">
        {installInfo} · Élec sur {periodLabels[periodIndex].toLowerCase()} = moyenne quotidienne × jours.
      </div>
    </div>
  );
};

export default HeatingPage;
